import { createApp, reactive } from 'vue'
import App from './App.vue'
import { createRouter, createWebHistory } from 'vue-router'
import { detectTenant } from './core/tenant'
import './assets/main.css'
import { tenantThemes, type Tenant } from './tenants'
import i18n from './i18n'
import Antd from 'ant-design-vue'
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { registerAuthGuard } from './core/guard'
import liff from '@line/liff'
import { bootstrapAuth, setRouter } from './utils/liff'
import VueApexCharts from 'vue3-apexcharts'
import { rollbar } from './utils/rollbar'

async function bootstrap() {
  if (import.meta.env.DEV) {
    // const VConsole = (await import('vconsole')).default
    //  new VConsole()
  }

  const tenant: Tenant = detectTenant() as Tenant

  const { default: tenantConfigRaw } = await import(`./tenants/${tenant}/config.ts`)
  const tenantConfig = reactive(tenantConfigRaw)

  const { default: routes } = await import(`./tenants/${tenant}/routes.ts`)

  tenantThemes[tenant]().then(() => {
    console.log(`${tenant} theme loaded`)
  })

  await loadLiff()

  const app = createApp(App)
  app.config.errorHandler = (err, _instance, info) => {
    rollbar.error(err as Error, { vueInfo: info })
  }

  const pinia = createPinia()
  app.use(pinia)

  app.provide('tenantConfig', tenantConfig)
  app.provide('tenantId', tenant)

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
  })

  const vueQueryOptions: VueQueryPluginOptions = {
    queryClientConfig: {
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 1,
          staleTime: 1000 * 60 * 5,
          gcTime: 1000 * 60 * 10,
        },
      },
    },
  }

  app.use(router)
  app.use(Antd)
  app.use(i18n)
  app.use(VueQueryPlugin, vueQueryOptions)
  app.use(VueApexCharts)

  registerAuthGuard(router)

  setRouter(router)
  // Kick off LIFF init + server-side login validation without blocking mount —
  // the guard (src/core/guard.ts) awaits this same promise before deciding
  // whether a protected route is authenticated.
  bootstrapAuth(app)

  app.mount('#app')

  document.title = `${tenantConfig.name}`
}

bootstrap()

async function loadLiff() {
  if (typeof liff !== 'undefined' && liff) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof liff !== 'undefined' && (liff as any)?.isInClient && (liff as any).isInClient()) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://static.line-scdn.net/liff/edge/2/sdk.js'
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }
}
